import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, ExternalLink, ImagePlus, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, resolveMediaUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const STORE_TYPES = [
  { value: "electronics", label: "Electronics / CCTV / signage" },
  { value: "cloth", label: "Cloth / fashion" },
  { value: "grocery", label: "Grocery / daily needs" },
  { value: "hardware", label: "Hardware / electrical" },
  { value: "general", label: "General retail" },
];

const PRODUCT_EMPTY = {
  name: "",
  brand: "",
  category_id: "",
  subcategory: "",
  image_url: "",
  image_urls_text: "",
  mrp: "",
  selling_price: "",
  offer_price: "",
  stock: 0,
  sku: "",
  model_number: "",
  warranty: "",
  specifications_text: "",
  description: "",
  tags: "",
  seo_title: "",
  seo_description: "",
  seo_slug: "",
  is_published: true,
  is_featured: false,
  is_bestseller: false,
  is_new_arrival: false,
};

const CATEGORY_EMPTY = { name: "", parent_id: "", sort_order: 0, is_active: true, seo_title: "", seo_description: "", seo_slug: "" };

function slugify(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48);
}

function parseSpecifications(text) {
  const spec = {};
  String(text || "").split(/\n+/).forEach((line) => {
    const [rawKey, ...rest] = line.split(":");
    const key = String(rawKey || "").trim();
    const value = rest.join(":").trim();
    if (key && value) spec[key] = value;
  });
  return spec;
}

function productToForm(product) {
  const specs = product.specifications && typeof product.specifications === "object"
    ? Object.entries(product.specifications).map(([k, v]) => `${k}: ${v}`).join("\n")
    : "";
  const images = product.image_urls?.length ? product.image_urls : (product.image_url ? [product.image_url] : []);
  return {
    ...PRODUCT_EMPTY,
    ...product,
    category_id: product.category_id || "",
    mrp: product.mrp ?? product.compare_at_price ?? "",
    selling_price: product.selling_price ?? product.price ?? "",
    offer_price: product.offer_price ?? "",
    image_urls_text: images.join("\n"),
    specifications_text: specs,
    tags: Array.isArray(product.tags) ? product.tags.join(", ") : (product.tags || ""),
  };
}

function money(value) {
  return `₹ ${Number(value || 0).toLocaleString()}`;
}

export default function RetailStorefrontPanel() {
  const [me, setMe] = useState(null);
  const [profile, setProfile] = useState({ headline: "", store_type: "electronics", description: "", hours: "", is_open: true, image_url: "", announcement: "", whatsapp_number: "", banner_slides: [] });
  const [seoForm, setSeoForm] = useState({ seo_title: "", meta_description: "", meta_keywords: "", canonical_url: "", og_image_url: "", business_address: "", google_map_url: "" });
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [productOpen, setProductOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [productForm, setProductForm] = useState(PRODUCT_EMPTY);
  const [categoryForm, setCategoryForm] = useState(CATEGORY_EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  const publicSlug = useMemo(() => slugify(me?.public_booking_slug || me?.name || "") || me?.id || "", [me]);
  const storeUrl = useMemo(() => {
    if (me?.custom_booking_domain_status === "active" && me?.custom_booking_domain) return `https://${me.custom_booking_domain}`;
    return publicSlug ? `${window.location.origin}/store/${encodeURIComponent(publicSlug)}` : "";
  }, [me, publicSlug]);

  const ecommerceOn = me?.ecommerce_enabled === true;

  const load = useCallback(async () => {
    try {
      const [meRes, seoRes] = await Promise.all([
        api.get("/client/me"),
        api.get("/client/seo-settings").catch(() => ({ data: {} })),
      ]);
      setMe(meRes.data);
      const stored = meRes.data.retail_store_profile || {};
      setProfile({
        headline: stored.headline || "",
        store_type: stored.store_type || "electronics",
        description: stored.description || "",
        hours: stored.hours || "",
        is_open: stored.is_open !== false,
        image_url: stored.image_url || "",
        announcement: stored.announcement || "",
        whatsapp_number: stored.whatsapp_number || "",
        banner_slides: Array.isArray(stored.banner_slides) ? stored.banner_slides : [],
      });
      setSeoForm((prev) => ({ ...prev, ...(seoRes.data || {}) }));
    } catch {}
  }, []);

  const loadEcommerce = useCallback(async () => {
    if (!ecommerceOn) return;
    try {
      const [p, c, o] = await Promise.all([
        api.get("/client/products"),
        api.get("/client/categories"),
        api.get("/client/orders").catch(() => ({ data: [] })),
      ]);
      setProducts(p.data || []);
      setCategories(c.data || []);
      setOrders(o.data || []);
    } catch {}
  }, [ecommerceOn]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadEcommerce(); }, [loadEcommerce]);

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.put("/client/retail-store/profile", profile);
      toast.success("Retail storefront saved");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveSeo = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/client/seo-settings", seoForm);
      setSeoForm((prev) => ({ ...prev, ...(data || {}) }));
      toast.success("SEO settings saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openProduct = (product = null) => {
    setEditingProduct(product);
    setProductForm(product ? productToForm(product) : PRODUCT_EMPTY);
    setUploadingImages(false);
    setProductOpen(true);
  };

  const uploadProductImages = async (event) => {
    const input = event.currentTarget;
    const files = Array.from(input.files || []).slice(0, 10);
    input.value = "";
    if (!files.length) return;
    const allowed = new Set(["image/jpeg", "image/png", "image/webp"]);
    const invalid = files.find((file) => !allowed.has(file.type));
    if (invalid) {
      toast.error("Please select JPG, PNG or WebP image files only.");
      return;
    }

    setUploadingImages(true);
    const uploadedUrls = [];
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("zone", "main");
        formData.append("folder", "ecommerce-products");
        formData.append("name", file.name);
        const { data } = await api.post("/client/media", formData);
        const url = data?.public_url || (data?.id ? `/api/media/serve/${data.id}` : "");
        if (url) uploadedUrls.push(url);
      }
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || "Could not upload the product image.");
    } finally {
      setUploadingImages(false);
    }
    if (uploadedUrls.length) {
      setProductForm((current) => {
        const existing = String(current.image_urls_text || "").split(/\n+/).map((value) => value.trim()).filter(Boolean);
        return { ...current, image_urls_text: [...new Set([...existing, ...uploadedUrls])].join("\n") };
      });
      toast.success(`${uploadedUrls.length} product image${uploadedUrls.length === 1 ? "" : "s"} uploaded`);
    }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    const images = String(productForm.image_urls_text || productForm.image_url || "").split(/\n+/).map((v) => v.trim()).filter(Boolean);
    const selling = Number(productForm.selling_price || productForm.offer_price || 0);
    const payload = {
      name: productForm.name,
      brand: productForm.brand,
      category_id: productForm.category_id || null,
      subcategory: productForm.subcategory,
      image_url: images[0] || productForm.image_url || "",
      image_urls: images,
      price: Number(productForm.offer_price || selling || 0),
      selling_price: selling,
      offer_price: productForm.offer_price === "" ? null : Number(productForm.offer_price || 0),
      mrp: productForm.mrp === "" ? null : Number(productForm.mrp || 0),
      compare_at_price: productForm.mrp === "" ? null : Number(productForm.mrp || 0),
      stock: productForm.stock,
      sku: productForm.sku,
      model_number: productForm.model_number,
      warranty: productForm.warranty,
      specifications: parseSpecifications(productForm.specifications_text),
      description: productForm.description,
      tags: String(productForm.tags || "").split(/[;,|]/).map((v) => v.trim()).filter(Boolean),
      seo_title: productForm.seo_title,
      seo_description: productForm.seo_description,
      seo_slug: productForm.seo_slug,
      is_published: productForm.is_published !== false,
      is_featured: !!productForm.is_featured,
      is_bestseller: !!productForm.is_bestseller,
      is_new_arrival: !!productForm.is_new_arrival,
    };
    setBusy(true);
    try {
      if (editingProduct) await api.put(`/client/products/${editingProduct.id}`, payload);
      else await api.post("/client/products", payload);
      toast.success(editingProduct ? "Product updated" : "Product added");
      setProductOpen(false);
      loadEcommerce();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteProduct = async (product) => {
    if (!window.confirm(`Delete ${product.name}?`)) return;
    try {
      await api.delete(`/client/products/${product.id}`);
      toast.success("Product deleted");
      loadEcommerce();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const openCategory = (cat = null) => {
    setEditingCategory(cat);
    setCategoryForm(cat ? { ...CATEGORY_EMPTY, ...cat, parent_id: cat.parent_id || "" } : CATEGORY_EMPTY);
    setCategoryOpen(true);
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    const payload = { ...categoryForm, parent_id: categoryForm.parent_id || null, sort_order: Number(categoryForm.sort_order || 0) };
    setBusy(true);
    try {
      if (editingCategory) await api.put(`/client/categories/${editingCategory.id}`, payload);
      else await api.post("/client/categories", payload);
      toast.success(editingCategory ? "Category updated" : "Category added");
      setCategoryOpen(false);
      loadEcommerce();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete category ${cat.name}?`)) return;
    try {
      await api.delete(`/client/categories/${cat.id}`);
      toast.success("Category deleted");
      loadEcommerce();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const copyStoreUrl = async () => {
    if (!storeUrl) return;
    try {
      await navigator.clipboard.writeText(storeUrl);
      toast.success("Store URL copied");
    } catch {
      toast.error("Could not copy URL");
    }
  };

  if (!me) {
    return <div className="bg-white border border-[#E5E7EB] rounded-sm p-6 text-sm text-[#6B7280]">Loading Retail Store...</div>;
  }

  if (!ecommerceOn) {
    return (
      <div className="space-y-4">
        <div className="bg-white border border-[#E5E7EB] rounded-sm p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Retail Store / Signage mode</div>
          <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight">Ecommerce is currently OFF.</h2>
          <p className="mt-2 text-sm text-[#6B7280] max-w-3xl">
            This Retail Store client can still use digital signage player, media, playlists, schedules and broadcast templates. Ask admin or dealer to enable ecommerce for product catalog, SEO, custom domain storefront, cart and enquiries.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/client/devices"><Button className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white">Manage signage screens</Button></Link>
            <Link to="/client/playlists"><Button variant="outline" className="rounded-sm">Broadcast playlists</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#E5E7EB] rounded-sm p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Public ecommerce URL</div>
          <div className="mt-1 font-mono text-sm break-all">{storeUrl}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" onClick={copyStoreUrl}><Copy className="w-4 h-4 mr-2" /> Copy</Button>
          {storeUrl && <a href={storeUrl} target="_blank" rel="noreferrer"><Button className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"><ExternalLink className="w-4 h-4 mr-2" /> Open</Button></a>}
        </div>
      </div>

      <Tabs defaultValue="setup" className="space-y-4">
        <TabsList className="rounded-sm flex flex-wrap h-auto justify-start">
          <TabsTrigger value="setup">Store setup</TabsTrigger>
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="orders">Orders / enquiries</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="signage">Signage content</TabsTrigger>
        </TabsList>

        <TabsContent value="setup">
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Store headline</Label><Input value={profile.headline} onChange={(e) => setProfile({ ...profile, headline: e.target.value })} className="mt-1 rounded-sm" placeholder="Premium electronics and CCTV solutions" /></div>
              <div><Label>Retail layout / sector</Label><Select value={profile.store_type || "electronics"} onValueChange={(v) => setProfile({ ...profile, store_type: v })}><SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent>{STORE_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Hours</Label><Input value={profile.hours} onChange={(e) => setProfile({ ...profile, hours: e.target.value })} className="mt-1 rounded-sm" placeholder="Mon-Sat 10 AM - 8 PM" /></div>
              <div><Label>WhatsApp number</Label><Input value={profile.whatsapp_number} onChange={(e) => setProfile({ ...profile, whatsapp_number: e.target.value })} className="mt-1 rounded-sm" placeholder="+91 98765 43210" /></div>
              <div className="md:col-span-2"><Label>Logo / cover image URL</Label><Input value={profile.image_url} onChange={(e) => setProfile({ ...profile, image_url: e.target.value })} className="mt-1 rounded-sm" placeholder="https://..." /></div>
              <div className="md:col-span-2"><Label>Announcement ticker</Label><Input value={profile.announcement} onChange={(e) => setProfile({ ...profile, announcement: e.target.value })} className="mt-1 rounded-sm" placeholder="Festival offer live this week" /></div>
              <div className="md:col-span-2"><Label>Description</Label><Textarea value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })} rows={3} className="mt-1 rounded-sm" /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={profile.is_open !== false} onCheckedChange={(v) => setProfile({ ...profile, is_open: v })} />
              <span className="text-sm font-medium">Store open status</span>
            </div>
            <div>
              <Label>Offer banner slides</Label>
              <div className="mt-2 space-y-2">
                {(profile.banner_slides || []).map((slide, idx) => (
                  <div key={slide.id || idx} className="grid md:grid-cols-[1fr_1fr_1fr_auto] gap-2">
                    <Input value={slide.image_url || ""} onChange={(e) => setProfile((prev) => ({ ...prev, banner_slides: prev.banner_slides.map((s, i) => i === idx ? { ...s, image_url: e.target.value } : s) }))} placeholder="Banner image URL" className="rounded-sm" />
                    <Input value={slide.title || ""} onChange={(e) => setProfile((prev) => ({ ...prev, banner_slides: prev.banner_slides.map((s, i) => i === idx ? { ...s, title: e.target.value } : s) }))} placeholder="Title" className="rounded-sm" />
                    <Input value={slide.link_url || ""} onChange={(e) => setProfile((prev) => ({ ...prev, banner_slides: prev.banner_slides.map((s, i) => i === idx ? { ...s, link_url: e.target.value } : s) }))} placeholder="Link or search term" className="rounded-sm" />
                    <Button type="button" variant="outline" size="icon" className="rounded-sm" onClick={() => setProfile((prev) => ({ ...prev, banner_slides: prev.banner_slides.filter((_, i) => i !== idx) }))}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => setProfile((prev) => ({ ...prev, banner_slides: [...(prev.banner_slides || []), { id: String(Date.now()), image_url: "", title: "", subtitle: "", link_url: "", sort_order: prev.banner_slides?.length || 0, is_active: true }] }))}><Plus className="w-4 h-4 mr-2" /> Add banner</Button>
              </div>
            </div>
            <Button disabled={busy} onClick={saveProfile} className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white">Save setup</Button>
          </div>
        </TabsContent>

        <TabsContent value="products">
          <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
            <div className="p-4 flex items-center justify-between"><h3 className="font-display text-xl font-extrabold">Products</h3><Button onClick={() => openProduct()} className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"><Plus className="w-4 h-4 mr-2" /> Add product</Button></div>
            <Table>
              <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Category</TableHead><TableHead>Price</TableHead><TableHead>Flags</TableHead><TableHead className="text-right">Stock</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {products.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm text-[#6B7280]">No products yet.</TableCell></TableRow>}
                {products.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-sm bg-[#F3F4F6] overflow-hidden">{p.image_url && <img src={resolveMediaUrl(p.image_url)} alt="" className="w-full h-full object-cover" />}</div>
                        <div><div className="font-semibold">{p.name}</div><div className="text-xs text-[#6B7280]">{p.brand || "No brand"} {p.model_number ? `· ${p.model_number}` : ""}</div></div>
                      </div>
                    </TableCell>
                    <TableCell>{categories.find((c) => c.id === p.category_id)?.name || "-"}</TableCell>
                    <TableCell><div className="font-semibold">{money(p.price)}</div>{p.compare_at_price > p.price && <div className="text-xs text-[#6B7280] line-through">{money(p.compare_at_price)}</div>}</TableCell>
                    <TableCell className="text-xs">{[p.is_featured && "Featured", p.is_bestseller && "Best seller", p.is_new_arrival && "New"].filter(Boolean).join(", ") || "-"}</TableCell>
                    <TableCell className="text-right">{Number(p.stock) === -1 ? "Unlimited" : p.stock}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => openProduct(p)}><Pencil className="w-4 h-4" /></Button><Button variant="ghost" size="icon" className="text-red-600" onClick={() => deleteProduct(p)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="categories">
          <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
            <div className="p-4 flex items-center justify-between"><h3 className="font-display text-xl font-extrabold">Categories</h3><Button onClick={() => openCategory()} className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"><Plus className="w-4 h-4 mr-2" /> Add category</Button></div>
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Slug</TableHead><TableHead>SEO title</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {categories.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-[#6B7280]">No categories yet.</TableCell></TableRow>}
                {categories.map((cat) => (
                  <TableRow key={cat.id}><TableCell className="font-semibold">{cat.name}</TableCell><TableCell className="font-mono text-xs">{cat.slug || cat.seo_slug}</TableCell><TableCell>{cat.seo_title || "-"}</TableCell><TableCell>{cat.is_active === false ? "Hidden" : "Active"}</TableCell><TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => openCategory(cat)}><Pencil className="w-4 h-4" /></Button><Button variant="ghost" size="icon" className="text-red-600" onClick={() => deleteCategory(cat)}><Trash2 className="w-4 h-4" /></Button></TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
            <div className="p-4"><h3 className="font-display text-xl font-extrabold">Orders / enquiries</h3><p className="text-sm text-[#6B7280] mt-1">Customer cart submissions appear here. Payment gateway is not required.</p></div>
            <Table>
              <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Customer</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {orders.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-[#6B7280]">No enquiries yet.</TableCell></TableRow>}
                {orders.map((order) => (
                  <TableRow key={order.id}><TableCell className="font-mono text-xs">{order.order_no || order.id}</TableCell><TableCell>{order.customer_label || order.shopper_name || order.shopper_phone || "-"}</TableCell><TableCell>{order.item_count || order.items?.length || 0}</TableCell><TableCell>{money(order.total)}</TableCell><TableCell>{order.status}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="seo">
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Meta title</Label><Input value={seoForm.seo_title || ""} onChange={(e) => setSeoForm({ ...seoForm, seo_title: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Canonical URL</Label><Input value={seoForm.canonical_url || ""} onChange={(e) => setSeoForm({ ...seoForm, canonical_url: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div className="md:col-span-2"><Label>Meta description</Label><Textarea value={seoForm.meta_description || ""} onChange={(e) => setSeoForm({ ...seoForm, meta_description: e.target.value })} rows={2} className="mt-1 rounded-sm" /></div>
              <div className="md:col-span-2"><Label>Meta keywords</Label><Input value={seoForm.meta_keywords || ""} onChange={(e) => setSeoForm({ ...seoForm, meta_keywords: e.target.value })} className="mt-1 rounded-sm" placeholder="cctv camera, led tv, signage player" /></div>
              <div className="md:col-span-2"><Label>OG image URL</Label><Input value={seoForm.og_image_url || ""} onChange={(e) => setSeoForm({ ...seoForm, og_image_url: e.target.value })} className="mt-1 rounded-sm" /></div>
            </div>
            <Button disabled={busy} onClick={saveSeo} className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white">Save SEO settings</Button>
          </div>
        </TabsContent>

        <TabsContent value="signage">
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
            <h3 className="font-display text-xl font-extrabold">Use ecommerce content in signage</h3>
            <p className="mt-2 text-sm text-[#6B7280] max-w-3xl">Products marked Featured, Best seller, New arrival, or carrying offer prices can be reused in signage zones for offer tickers, product highlights, and screen campaigns. Use Devices, Media, Templates, Playlists and Schedules for broadcast delivery.</p>
            <div className="mt-4 flex flex-wrap gap-3"><Link to="/client/templates"><Button variant="outline" className="rounded-sm">Templates</Button></Link><Link to="/client/playlists"><Button variant="outline" className="rounded-sm">Playlists</Button></Link><Link to="/client/schedules"><Button variant="outline" className="rounded-sm">Schedules</Button></Link></div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={productOpen} onOpenChange={setProductOpen}>
        <DialogContent className="rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingProduct ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
          <form onSubmit={saveProduct} className="grid md:grid-cols-2 gap-4">
            <div><Label>Product name</Label><Input required value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>Brand</Label><Input value={productForm.brand} onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>Category</Label><Select value={productForm.category_id || "none"} onValueChange={(v) => setProductForm({ ...productForm, category_id: v === "none" ? "" : v })}><SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">No category</SelectItem>{categories.map((cat) => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Subcategory</Label><Input value={productForm.subcategory} onChange={(e) => setProductForm({ ...productForm, subcategory: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>MRP</Label><Input type="number" value={productForm.mrp} onChange={(e) => setProductForm({ ...productForm, mrp: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>Selling price</Label><Input required type="number" value={productForm.selling_price} onChange={(e) => setProductForm({ ...productForm, selling_price: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>Offer price</Label><Input type="number" value={productForm.offer_price} onChange={(e) => setProductForm({ ...productForm, offer_price: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>Stock</Label><Input value={productForm.stock} onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })} className="mt-1 rounded-sm" placeholder="-1 for unlimited" /></div>
            <div><Label>SKU</Label><Input value={productForm.sku} onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>Model number</Label><Input value={productForm.model_number} onChange={(e) => setProductForm({ ...productForm, model_number: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>Warranty</Label><Input value={productForm.warranty} onChange={(e) => setProductForm({ ...productForm, warranty: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>SEO slug</Label><Input value={productForm.seo_slug} onChange={(e) => setProductForm({ ...productForm, seo_slug: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div className="space-y-2 md:col-span-2">
              <Label>Product images</Label>
              <div className="flex flex-wrap items-center gap-3 rounded-sm border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-3">
                <label className={`inline-flex items-center gap-2 rounded-sm bg-[#111827] px-4 py-2 text-sm font-semibold text-white ${uploadingImages ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-[#374151]"}`}>
                  {uploadingImages ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                  {uploadingImages ? "Uploading…" : "Upload from computer"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={uploadingImages} onChange={uploadProductImages} className="sr-only" data-testid="product-image-upload" />
                </label>
                <span className="text-xs text-[#64748B]">JPG, PNG or WebP. You can select up to 10 images.</span>
              </div>
              <Textarea value={productForm.image_urls_text} onChange={(e) => setProductForm({ ...productForm, image_urls_text: e.target.value })} rows={3} className="rounded-sm" placeholder="Or enter one image URL per line" />
              {String(productForm.image_urls_text || "").split(/\n+/).map((value) => value.trim()).filter(Boolean).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {String(productForm.image_urls_text || "").split(/\n+/).map((value) => value.trim()).filter(Boolean).slice(0, 10).map((url, index) => <img key={`${url}-${index}`} src={resolveMediaUrl(url)} alt={`Product preview ${index + 1}`} className="h-16 w-16 rounded-sm border border-[#E5E7EB] bg-white object-contain" />)}
                </div>
              )}
            </div>
            <div className="md:col-span-2"><Label>Specifications</Label><Textarea value={productForm.specifications_text} onChange={(e) => setProductForm({ ...productForm, specifications_text: e.target.value })} rows={4} className="mt-1 rounded-sm" placeholder={"Resolution: 4MP\nLens: 3.6mm\nConnectivity: PoE"} /></div>
            <div className="md:col-span-2"><Label>Description</Label><Textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} rows={3} className="mt-1 rounded-sm" /></div>
            <div className="md:col-span-2"><Label>Tags</Label><Input value={productForm.tags} onChange={(e) => setProductForm({ ...productForm, tags: e.target.value })} className="mt-1 rounded-sm" placeholder="cctv, nvr, ip camera" /></div>
            <div><Label>SEO title</Label><Input value={productForm.seo_title} onChange={(e) => setProductForm({ ...productForm, seo_title: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>SEO description</Label><Input value={productForm.seo_description} onChange={(e) => setProductForm({ ...productForm, seo_description: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div className="md:col-span-2 grid sm:grid-cols-4 gap-3">
              {[["is_published", "Published"], ["is_featured", "Featured"], ["is_bestseller", "Best seller"], ["is_new_arrival", "New arrival"]].map(([key, label]) => <label key={key} className="flex items-center gap-2 rounded-sm border border-[#E5E7EB] p-3 text-sm"><Switch checked={productForm[key] !== false && !!productForm[key]} onCheckedChange={(v) => setProductForm({ ...productForm, [key]: v })} /> {label}</label>)}
            </div>
            <DialogFooter className="md:col-span-2"><Button type="button" variant="outline" className="rounded-sm" onClick={() => setProductOpen(false)}>Cancel</Button><Button disabled={busy || uploadingImages} className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white">{editingProduct ? "Save" : "Add product"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={categoryOpen} onOpenChange={setCategoryOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle>{editingCategory ? "Edit category" : "Add category"}</DialogTitle></DialogHeader>
          <form onSubmit={saveCategory} className="space-y-4">
            <div><Label>Name</Label><Input required value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>SEO slug</Label><Input value={categoryForm.seo_slug || ""} onChange={(e) => setCategoryForm({ ...categoryForm, seo_slug: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>SEO title</Label><Input value={categoryForm.seo_title || ""} onChange={(e) => setCategoryForm({ ...categoryForm, seo_title: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>SEO description</Label><Textarea value={categoryForm.seo_description || ""} onChange={(e) => setCategoryForm({ ...categoryForm, seo_description: e.target.value })} rows={2} className="mt-1 rounded-sm" /></div>
            <div className="flex items-center gap-3"><Switch checked={categoryForm.is_active !== false} onCheckedChange={(v) => setCategoryForm({ ...categoryForm, is_active: v })} /><span className="text-sm font-medium">Active</span></div>
            <DialogFooter><Button type="button" variant="outline" className="rounded-sm" onClick={() => setCategoryOpen(false)}>Cancel</Button><Button disabled={busy} className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white">{editingCategory ? "Save" : "Add category"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
